import React, { useState } from "react";
import {
  Paper,
  Typography,
  TextField,
  Button,
  Container,
  Box,
  makeStyles,
  InputAdornment,
  IconButton,
  Divider,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Checkbox,
  Link,
} from "@material-ui/core";
import { Alert } from "@material-ui/lab";
import { Visibility, VisibilityOff, Email } from "@material-ui/icons";
import { useAuth } from "../context/ApiKeyContext";
import { useTranslation } from "../hooks/useTranslation";
import LanguageSelector from "./LanguageSelector";
import ThemeSelector from "./ThemeSelector";
import { getApiBaseUrl } from "../config";

const API_BASE_URL = getApiBaseUrl();

const useStyles = makeStyles((theme) => ({
  root: {
    marginTop: theme.spacing(8),
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    position: "relative",
  },
  paper: {
    padding: theme.spacing(4),
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    maxWidth: 500,
    margin: "0 auto",
  },
  formField: {
    marginBottom: theme.spacing(3),
    width: "100%",
  },
  submit: {
    margin: theme.spacing(3, 0, 2),
  },
  forgotPassword: {
    marginTop: theme.spacing(2),
    textAlign: "center",
  },
  icon: {
    fontSize: 40,
    marginBottom: theme.spacing(1),
    color: theme.palette.primary.main,
  },
  title: {
    marginBottom: theme.spacing(3),
  },
  infoBox: {
    marginTop: theme.spacing(4),
    padding: theme.spacing(2),
    backgroundColor: "var(--ikaro-bg-surface-secondary)",
    borderRadius: theme.shape.borderRadius,
    border: "1px solid var(--ikaro-border-default)",
  },
  dialogContent: {
    minWidth: 400,
    overflow: "auto",
  },
  passwordRequirement: {
    fontSize: "0.75rem",
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(1),
  },
  termsDialog: {
    minWidth: 600,
    maxHeight: 400,
    overflow: "auto",
  },
  languageSelector: {
    position: "absolute",
    top: theme.spacing(2),
    right: theme.spacing(2),
    zIndex: 1000,
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  paperWithLanguage: {
    position: "relative",
  },
}));

// Componente para o modal "Esqueceu sua senha?" - pede o email
const ForgotPasswordDialog = ({
  open,
  onClose,
  onSend,
  isLoading,
  error,
  initialEmail = "",
}) => {
  const classes = useStyles();
  const { t } = useTranslation("login");
  const [email, setEmail] = useState(initialEmail);

  React.useEffect(() => {
    if (open) {
      setEmail(initialEmail);
    }
  }, [open, initialEmail]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (email.trim()) {
      onSend(email.trim());
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{t("forgotPasswordTitle")}</DialogTitle>
      <DialogContent className={classes.dialogContent}>
        <Typography variant="body2" color="textSecondary" className="mb-16">
          {t("forgotPasswordDescription")}
        </Typography>
        <form onSubmit={handleSubmit} id="forgot-password-form">
          {error && (
            <Alert severity="error" className="w-full mb-16">
              {error}
            </Alert>
          )}
          <TextField
            variant="outlined"
            margin="normal"
            required
            fullWidth
            id="forgot-email"
            label={t("email")}
            name="email"
            autoComplete="email"
            autoFocus
            className={classes.formField}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            type="email"
          />
        </form>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isLoading}>
          {t("cancel")}
        </Button>
        <Button
          type="submit"
          form="forgot-password-form"
          color="primary"
          variant="contained"
          disabled={isLoading || !email.trim()}
        >
          {isLoading ? t("signingIn") : t("sendRecoveryEmail")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Componente para o popup de termos
const TermsDialog = ({ open, onClose }) => {
  const classes = useStyles();
  const { t } = useTranslation("login");

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{t("terms.title")}</DialogTitle>
      <DialogContent className={classes.termsDialog}>
        <Typography variant="body2" paragraph>
          <strong>{t("terms.header")}</strong>
        </Typography>

        <Typography variant="body2" paragraph>
          {t("terms.intro")}
        </Typography>

        <Typography variant="body2" paragraph>
          <strong>{t("terms.section1.title")}</strong>
          <br />
          {t("terms.section1.content")}
        </Typography>

        <Typography variant="body2" paragraph>
          <strong>{t("terms.section2.title")}</strong>
          <br />
          {t("terms.section2.content")}
        </Typography>

        <Typography variant="body2" paragraph>
          <strong>{t("terms.section3.title")}</strong>
          <br />
          {t("terms.section3.content")}
        </Typography>

        <Typography variant="body2" paragraph>
          <strong>{t("terms.section4.title")}</strong>
          <br />
          {t("terms.section4.content")}
        </Typography>

        <Typography variant="body2" paragraph>
          <strong>{t("terms.section5.title")}</strong>
          <br />
          {t("terms.section5.content")}
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="primary">
          {t("close")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Componente para o popup de troca de senha
const PasswordChangeDialog = ({
  open,
  onClose,
  onPasswordChanged,
  userEmail,
}) => {
  const classes = useStyles();
  const { t } = useTranslation("login");
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const validatePassword = (password) => {
    if (!password) {
      return { isValid: false, error: t("passwordChange.allFieldsRequired") };
    }
    if (password.length < 8) {
      return { isValid: false, error: t("passwordValidation.minLength") };
    }
    if (!/[a-z]/.test(password)) {
      return { isValid: false, error: t("passwordValidation.lowercase") };
    }
    if (!/[A-Z]/.test(password)) {
      return { isValid: false, error: t("passwordValidation.uppercase") };
    }
    if (!/\d/.test(password)) {
      return { isValid: false, error: t("passwordValidation.number") };
    }
    if (!/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(password)) {
      return { isValid: false, error: t("passwordValidation.special") };
    }
    return { isValid: true, error: "" };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!currentPassword || !newPassword || !confirmPassword) {
      setError(t("passwordChange.allFieldsRequired"));
      return;
    }

    const passwordValidation = validatePassword(newPassword);
    if (!passwordValidation.isValid) {
      setError(passwordValidation.error);
      return;
    }

    if (newPassword !== confirmPassword) {
      setError(t("passwordChange.passwordsNotMatch"));
      return;
    }

    if (!acceptTerms) {
      setError(t("passwordChange.acceptTermsRequired"));
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await fetch(`${API_BASE_URL}/users/change-password`, {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
        body: new URLSearchParams({
          email: userEmail,
          current_password: currentPassword,
          new_password: newPassword,
        }).toString(),
      });

      if (response.ok) {
        onPasswordChanged();
        onClose();
      } else {
        const errorData = await response.json();
        setError(
          errorData.detail ||
            "Erro ao alterar senha. Verifique se a senha atual está correta.",
        );
      }
    } catch (error) {
      console.error("Erro ao alterar senha:", error);
      setError(t("passwordChange.changePasswordError"));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle>{t("passwordChange.title")}</DialogTitle>
        <DialogContent className={classes.dialogContent}>
          {error && (
            <Alert severity="error" className="mb-16">
              {error}
            </Alert>
          )}

          <form onSubmit={handleSubmit}>
            <TextField
              variant="outlined"
              margin="normal"
              required
              fullWidth
              label={t("passwordChange.currentPassword")}
              type={showCurrentPassword ? "text" : "password"}
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() =>
                        setShowCurrentPassword(!showCurrentPassword)
                      }
                      edge="end"
                    >
                      {showCurrentPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              variant="outlined"
              margin="normal"
              required
              fullWidth
              label={t("passwordChange.newPassword")}
              type={showNewPassword ? "text" : "password"}
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowNewPassword(!showNewPassword)}
                      edge="end"
                    >
                      {showNewPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              variant="outlined"
              margin="normal"
              required
              fullWidth
              label={t("passwordChange.repeatNewPassword")}
              type={showConfirmPassword ? "text" : "password"}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() =>
                        setShowConfirmPassword(!showConfirmPassword)
                      }
                      edge="end"
                    >
                      {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <Typography className={classes.passwordRequirement}>
              {t("passwordRequirements")}
            </Typography>

            <FormControlLabel
              control={
                <Checkbox
                  checked={acceptTerms}
                  onChange={(e) => setAcceptTerms(e.target.checked)}
                  name="acceptTerms"
                  color="primary"
                />
              }
              label={
                <Typography variant="body2">
                  {t("acceptTerms")}{" "}
                  <Link
                    component="button"
                    variant="body2"
                    onClick={(e) => {
                      e.preventDefault();
                      setShowTerms(true);
                    }}
                  >
                    {t("passwordChange.termsAcceptance")}
                  </Link>
                </Typography>
              }
              className="mt-16 mb-8"
            />
          </form>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>{t("cancel")}</Button>
          <Button
            onClick={handleSubmit}
            color="primary"
            disabled={!acceptTerms || isSubmitting}
            variant="contained"
          >
            {isSubmitting
              ? t("passwordChange.changingPassword")
              : t("passwordChange.changePassword")}
          </Button>
        </DialogActions>
      </Dialog>

      <TermsDialog open={showTerms} onClose={() => setShowTerms(false)} />
    </>
  );
};

const LoginPage = ({ onLoginSuccess }) => {
  const classes = useStyles();
  const { t } = useTranslation("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [forgotPasswordSent, setForgotPasswordSent] = useState(false);
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState("");
  const [showForgotPasswordDialog, setShowForgotPasswordDialog] =
    useState(false);
  const [forgotPasswordError, setForgotPasswordError] = useState("");
  const [showPasswordChange, setShowPasswordChange] = useState(false);
  const [pendingAuth, setPendingAuth] = useState(null);
  const [rememberMe, setRememberMe] = useState(false);

  const { saveAuthData } = useAuth();

  // Carregar email salvo ao iniciar
  React.useEffect(() => {
    const savedEmail = localStorage.getItem("rememberedEmail");
    if (savedEmail) {
      setEmail(savedEmail);
      setRememberMe(true);
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setIsLoading(true);

    if (!email.trim() || !password.trim()) {
      setError(t("emptyFieldsError"));
      setIsLoading(false);
      return;
    }

    // Gerenciar o email lembrado
    if (rememberMe) {
      localStorage.setItem("rememberedEmail", email);
    } else {
      localStorage.removeItem("rememberedEmail");
    }

    try {
      const response = await fetch(`${API_BASE_URL}/users/login`, {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
        body: new URLSearchParams({
          username: email,
          password: password,
        }).toString(),
      });

      if (response.ok) {
        const data = await response.json();

        // Verificar se é necessário trocar a senha
        if (data.force_password_change) {
          setPendingAuth(data);
          setShowPasswordChange(true);
        } else {
          // Salvar dados de autenticação
          saveAuthData(data.token, {
            email,
            name: data.name,
            user_id: data.user_id,
            permissions: data.permissions,
            product_permissions: data.product_permissions || [],
            cells: data.cells,
            company_name: data.company_name,
            tenant_name: data.tenant_name,
            tenant_id: data.tenant_id,
          });

          // A validação e seleção de célula agora é feita automaticamente
          // no contexto através da função loadUserCell chamada em saveAuthData

          // Chamar a função de callback para indicar login bem-sucedido
          if (onLoginSuccess) {
            onLoginSuccess();
          }
        }
      } else {
        if (response.status === 429) {
          setError(t("accountBlocked"));
        } else if (response.status === 403) {
          let message = t("cellAccessDenied");
          try {
            const errBody = await response.json();
            const d = errBody.detail;
            if (d?.code === "user_deactivated") {
              message = t("userDeactivated");
            } else if (d?.code === "cells_access_denied") {
              message = t("cellAccessDenied");
            } else if (typeof d === "string") {
              message = d;
            } else if (Array.isArray(d) && d.length > 0) {
              const parts = d
                .map((item) => (typeof item === "string" ? item : item?.msg))
                .filter(Boolean);
              if (parts.length) message = parts.join(" ");
            }
          } catch (_) {
            /* manter fallback i18n */
          }
          setError(message);
        } else if (response.status === 401) {
          setError(t("invalidCredentials"));
        } else {
          setError(t("loginError"));
        }
      }
    } catch (error) {
      console.error("Erro ao fazer login:", error);
      setError(t("loginError"));
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPassword = async (emailToUse) => {
    if (!emailToUse?.trim()) {
      setForgotPasswordError(t("passwordResetRequestError"));
      return;
    }

    setIsLoading(true);
    setForgotPasswordError("");

    try {
      const response = await fetch(`${API_BASE_URL}/users/forgot_password`, {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
        body: new URLSearchParams({
          email: emailToUse.trim(),
        }).toString(),
      });

      if (response.ok) {
        setForgotPasswordEmail(emailToUse.trim());
        setShowForgotPasswordDialog(false);
        setForgotPasswordSent(true);
      } else {
        setForgotPasswordError(t("passwordResetError"));
      }
    } catch (error) {
      console.error("Erro ao solicitar recuperação de senha:", error);
      setForgotPasswordError(t("passwordResetError"));
    } finally {
      setIsLoading(false);
    }
  };

  const handlePasswordChanged = () => {
    // Após trocar a senha, salvar os dados de autenticação pendente
    if (pendingAuth) {
      saveAuthData(pendingAuth.token, {
        email,
        name: pendingAuth.name,
        user_id: pendingAuth.user_id,
        permissions: pendingAuth.permissions,
        product_permissions: pendingAuth.product_permissions || [],
        cells: pendingAuth.cells,
        company_name: pendingAuth.company_name,
        tenant_name: pendingAuth.tenant_name,
        tenant_id: pendingAuth.tenant_id,
      });

      if (onLoginSuccess) {
        onLoginSuccess();
      }
    }
  };

  if (forgotPasswordSent) {
    return (
      <Container component="main" maxWidth="md">
        <div className={classes.root}>
          <Paper
            elevation={3}
            className={`${classes.paper} ${classes.paperWithLanguage}`}
          >
            {/* Seletor de língua e tema */}
            <div className={classes.languageSelector}>
              <ThemeSelector variant="login" />
              <LanguageSelector variant="login" />
            </div>
            <Email className={classes.icon} />
            <Typography component="h1" variant="h5" className={classes.title}>
              {t("emailSent")}
            </Typography>

            <Alert severity="success" className="w-full mb-16">
              {t("emailSentMessage", { email: forgotPasswordEmail })}
            </Alert>

            <Button
              fullWidth
              variant="contained"
              color="primary"
              onClick={() => setForgotPasswordSent(false)}
              className={classes.submit}
            >
              {t("backToLogin")}
            </Button>
          </Paper>
        </div>
      </Container>
    );
  }

  return (
    <Container component="main" maxWidth="md">
      <div className={classes.root}>
        <Paper
          elevation={3}
          className={`${classes.paper} ${classes.paperWithLanguage}`}
        >
          {/* Seletor de língua e tema */}
          <div className={classes.languageSelector}>
            <ThemeSelector variant="login" />
            <LanguageSelector variant="login" />
          </div>
          <Typography component="h1" variant="h5" className={classes.title}>
            <img
              src="https://elaw.com.br/wp-content/uploads/2024/08/logo_elaw.png"
              alt="eLaw GenAI"
              className={classes.logo}
            />{" "}
            GenAI
          </Typography>

          <Divider className="w-full mb-20" />

          {error && (
            <Alert severity="error" className="w-full mb-16">
              {error}
            </Alert>
          )}

          <form onSubmit={handleSubmit} className="w-full">
            <TextField
              variant="outlined"
              margin="normal"
              required
              fullWidth
              id="email"
              label={t("email")}
              name="email"
              autoComplete="email"
              autoFocus
              className={classes.formField}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              type="email"
            />

            <TextField
              variant="outlined"
              margin="normal"
              required
              fullWidth
              name="password"
              label={t("password")}
              type={showPassword ? "text" : "password"}
              id="password"
              autoComplete="current-password"
              className={classes.formField}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <FormControlLabel
              control={
                <Checkbox
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  color="primary"
                />
              }
              label={t("rememberEmail")}
              className="mb-16"
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              color="primary"
              className={classes.submit}
              disabled={isLoading}
            >
              {isLoading ? t("signingIn") : t("signIn")}
            </Button>

            <div className={classes.forgotPassword}>
              <Link
                component="button"
                variant="body2"
                onClick={(e) => {
                  e.preventDefault();
                  setForgotPasswordError("");
                  setShowForgotPasswordDialog(true);
                }}
                disabled={isLoading}
              >
                {t("forgotPassword")}
              </Link>
            </div>
          </form>

          <ForgotPasswordDialog
            open={showForgotPasswordDialog}
            onClose={() => {
              setShowForgotPasswordDialog(false);
              setForgotPasswordError("");
            }}
            onSend={handleForgotPassword}
            isLoading={isLoading}
            error={forgotPasswordError}
            initialEmail={email}
          />

          <Box className={classes.infoBox}>
            <Typography variant="subtitle2" color="textSecondary" gutterBottom>
              {t("dontHaveAccount")}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Entre em contato com nossa equipe para criar sua conta.
            </Typography>
          </Box>
        </Paper>

        <Grid container className="mt-40">
          <Grid item xs={12}>
            <Typography
              variant="caption"
              color="textSecondary"
              align="center"
              component="p"
            >
              &copy; {new Date().getFullYear()} eLaw GenAI. Todos os direitos
              reservados.
            </Typography>
          </Grid>
        </Grid>
      </div>

      <PasswordChangeDialog
        open={showPasswordChange}
        onClose={() => setShowPasswordChange(false)}
        onPasswordChanged={handlePasswordChanged}
        userEmail={email}
      />
    </Container>
  );
};

export default LoginPage;
